import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  Boxes,
  Building2,
  Truck,
  Monitor,
  Armchair,
  TrendingDown,
  DollarSign,
  Calendar,
  MapPin,
  Eye,
  RefreshCw } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const categoryIcons = {
  'IT Equipment': Monitor,
  'Vehicles': Truck,
  'Manufacturing': Boxes,
  'Furniture': Armchair
};

const statusConfig = {
  active: { label: 'Active', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' },
  maintenance: { label: 'Maintenance', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' },
  disposed: { label: 'Disposed', class: 'bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-400' }
};

export const FixedAssets = () => {const { t } = useTranslation("finance");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/finance/fixed-assets`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch assets');
      const data = await response.json();
      setAssets(data);
    } catch (err) {
      toast.error('Failed to load fixed assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [activeTab]);

  const stats = {
    totalAssets: assets.filter((a) => a.status === 'active').length,
    totalValue: assets.filter((a) => a.status === 'active').reduce((sum, a) => sum + (a.current_value || 0), 0),
    purchaseCost: assets.filter((a) => a.status === 'active').reduce((sum, a) => sum + (a.purchase_cost || 0), 0),
    totalDepreciation: assets.filter((a) => a.status === 'active').reduce((sum, a) => sum + ((a.purchase_cost || 0) - (a.current_value || 0)), 0)
  };

  const filteredAssets = assets.filter((asset) => {
    if (!searchTerm) return true;
    return asset.asset_tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.category?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-fixed-assets">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("fixedAssets", "Fixed Assets")}</h1>
          <p className="text-muted-foreground mt-1">{t("trackCompanyProperty", "Track company property, equipment, and depreciation")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAssets} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" />{t("addAsset", "Add Asset")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <FinanceStatCard
          title="Total Assets"
          value={stats.totalAssets}
          icon={Boxes}
          color="blue" />
        
        <FinanceStatCard
          title="Current Value"
          value={stats.totalValue}
          icon={DollarSign}
          color="emerald"
          prefix="$" />
        
        <FinanceStatCard
          title="Purchase Cost"
          value={stats.purchaseCost}
          icon={DollarSign}
          color="gold"
          prefix="$" />
        
        <FinanceStatCard
          title="Total Depreciation"
          value={stats.totalDepreciation}
          icon={TrendingDown}
          color="red"
          prefix="$" />
        
      </div>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("allAssets", "All Assets")}</TabsTrigger>
                <TabsTrigger value="active">{t("active", "Active")}</TabsTrigger>
                <TabsTrigger value="maintenance">{t("maintenance", "Maintenance")}</TabsTrigger>
                <TabsTrigger value="disposed">{t("disposed", "Disposed")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("assetTag", "Asset Tag")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("name", "Name")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("category", "Category")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("location", "Location")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("purchaseCost", "Purchase Cost")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("currentValue", "Current Value")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("deprRate", "Depr. Rate")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset) => {
                  const CategoryIcon = categoryIcons[asset.category] || Boxes;
                  return (
                    <tr key={asset.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{asset.asset_tag}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{asset.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant="secondary">{asset.category}</Badge>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5 text-sm">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          {asset.location}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right font-mono">
                        ${asset.purchase_cost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-4 text-right font-mono font-medium text-emerald-600">
                        ${asset.current_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-sm">{asset.depreciation_rate}%</span>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={statusConfig[asset.status]?.class || statusConfig.active.class}>
                          {statusConfig[asset.status]?.label || asset.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>

          {filteredAssets.length === 0 &&
          <div className="py-12 text-center">
              <Boxes className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noAssetsFound", "No assets found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};